import * as React from "react";
import { FileText, Upload } from "lucide-react";

import { cn } from "@/lib/utils";
import { USER_ORIGIN_WEBAPP_LEGACY } from "@/lib/user-origin";
import { useAuth } from "@/contexts/auth-context";
import { Badge } from "@/components/ui/badge";
import { Banner } from "@/components/ui/banner";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { useLegacyCertStatus } from "@/hooks/queries/useLegacyCertStatus";
import { useUploadLegacyCert } from "@/hooks/queries/useUploadLegacyCert";
import type { LegacyCertStatusValue } from "@/hooks/queries/legacy-cert-types";
import {
  toCertStatusErrorMessage,
  toUploadCertErrorMessage,
} from "@/routes/profile/legacy-cert-error";
import { CLINICIAN_ROLES } from "@/routes/route-access";

// Story 16.3 — the Legacy AMPS certificate card. Rendered on /profile ONLY for a
// legacy CLINICIAN: the clinician role AND userOrigin === "webapp_legacy" (Decision 1
// / AC9 — the card hides itself for everyone else; the server is the authoritative
// gate, AC8). One component with a status branch driven by GET /profile/legacy-cert:
// Awaiting-upload, Pending, Verified (render-only — the transition is 16.4), and
// Rejected (render-only — the transition is 16.5). Maps Screen 14b/14 markup →
// tokens (never raw hex).

const ACCEPTED_MIME_TYPES = ["application/pdf", "image/jpeg", "image/png"] as const;
// Client-side pre-check only (fast feedback); the SERVER is authoritative (config-
// tunable CERT_MAX_FILE_SIZE_BYTES). Matches the 10 MB permissive default.
const MAX_BYTES = 10_000_000;
const FILE_ACCEPT = ".pdf,image/png,image/jpeg";
const HELPER_TEXT = "Accepted file types and maximum size are set by CITS.";

const CLIENT_TOO_LARGE = "That file is too large. Upload a smaller certificate file.";
const CLIENT_WRONG_TYPE = "That file type isn't accepted. Upload a PDF, JPG, or PNG.";
const CLIENT_NO_FILE = "Choose a certificate file to upload.";

const PILL: Record<
  LegacyCertStatusValue,
  { text: string; variant: "warning" | "neutral" }
> = {
  none: { text: "Awaiting upload", variant: "warning" },
  pending: { text: "Pending verification", variant: "warning" },
  verified: { text: "Verified", variant: "neutral" },
  rejected: { text: "Could not be verified", variant: "warning" },
};

function formatDate(iso: string | null | undefined): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export function LegacyCertCard() {
  const { user, hasRole } = useAuth();
  // ROLE is the clinical-entitlement predicate; ORIGIN is only the flavour of
  // clinician. Both must hold before this card — or its query — exists.
  const isClinician = hasRole(...CLINICIAN_ROLES);
  const isLegacy = user?.userOrigin === USER_ORIGIN_WEBAPP_LEGACY;

  const statusQuery = useLegacyCertStatus({ enabled: isClinician && isLegacy });
  const upload = useUploadLegacyCert();

  const [selectedFile, setSelectedFile] = React.useState<File | null>(null);
  const [clientError, setClientError] = React.useState<string | null>(null);
  const [dragActive, setDragActive] = React.useState(false);
  // For pending/rejected, the upload control is shown only after the user opts to
  // replace / re-upload. For "none" it is always shown.
  const [showUploadControl, setShowUploadControl] = React.useState(false);
  const inputRef = React.useRef<HTMLInputElement | null>(null);

  // AC9 — visibility gate, ROLE first and ORIGIN second, matching the shape
  // `AccessReadinessCard` and `SubscriptionSection` use on this same screen
  // (`if (!hasRole("clinician")) return null`). A POSITIVE allow-list on role:
  // "holds a prior AMPS certification" is a claim only a clinician can make, so
  // an org_admin or super_admin sees NO cert card whatever their origin reads.
  // That matters beyond the accounts this epic mints — every org_admin
  // provisioned before migration `m5userorigin001` keeps its historical
  // `webapp_legacy` value by design (Story 15.0b, D6), and origin alone would
  // still hand each of them a working upload into the CITS clinician review
  // queue. The server refuses the same pair (`_shared/legacy_guard.py`); this
  // is the visibility half, not the control.
  // The hooks above stay above this return, as React's rules of hooks require.
  if (!isClinician) return null;
  if (!isLegacy) return null;

  const status: LegacyCertStatusValue = statusQuery.data?.status ?? "none";
  const pill = PILL[status];
  // gh-742 — `status` above falls back to "none" whenever the payload is absent, which
  // is a real status for exactly one reason: the server said so. Everything that states
  // a fact about the certificate — the pill, the subtitle, and the upload funnel itself
  // — keys on THIS instead, so none of them asserts "Awaiting upload" before the server
  // has. Phrased as the POSITIVE `isSuccess`, not `!isLoading && !isError`: that double
  // negative reads as "the read is over" but is not, because `isLoading` is
  // `isPending && isFetching` — a query that is PAUSED (offline, networkMode "online")
  // or disabled has never started, so both flags are false while `data` is undefined,
  // and the fallback would render the whole gh-742 funnel at an approved clinician who
  // merely lost the network.
  const statusKnown = statusQuery.isSuccess;
  const showControl = statusKnown && (status === "none" || showUploadControl);

  const openControl = () => {
    setClientError(null);
    upload.reset();
    setShowUploadControl(true);
  };

  const cancelControl = () => {
    setSelectedFile(null);
    setClientError(null);
    upload.reset();
    setShowUploadControl(false);
    if (inputRef.current) inputRef.current.value = "";
  };

  const selectFile = (file: File | null) => {
    setClientError(null);
    upload.reset();
    setSelectedFile(file);
  };

  const onFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    selectFile(event.target.files?.[0] ?? null);
  };

  const onDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setDragActive(false);
    if (upload.isPending) return;
    const file = event.dataTransfer.files?.[0];
    if (file) selectFile(file);
  };

  const onUpload = () => {
    setClientError(null);
    if (!selectedFile) {
      setClientError(CLIENT_NO_FILE);
      return;
    }
    if (!ACCEPTED_MIME_TYPES.includes(selectedFile.type as (typeof ACCEPTED_MIME_TYPES)[number])) {
      setClientError(CLIENT_WRONG_TYPE);
      return;
    }
    if (selectedFile.size > MAX_BYTES) {
      setClientError(CLIENT_TOO_LARGE);
      return;
    }
    // On success the status query is invalidated → the card re-renders to Pending.
    // On error the card stays in its prior state (no optimistic change), so a
    // network/abort failure reverts cleanly (AC11) and the user can retry.
    upload.mutate(selectedFile, {
      onSuccess: () => {
        setSelectedFile(null);
        setShowUploadControl(false);
        if (inputRef.current) inputRef.current.value = "";
      },
    });
  };

  // Server-mapped upload error (size/type/unavailable/network) — never the server's
  // own message string. Client pre-check error takes precedence (it fired first).
  const errorMessage =
    clientError ?? (upload.isError ? toUploadCertErrorMessage(upload.error) : null);

  const uploadControl = (
    <div className="flex flex-col gap-3">
      <Banner variant="info">
        {status === "none"
          ? "Upload your certificate to start verification."
          : "Choose a new file to replace your certificate."}
      </Banner>
      <div className="flex flex-col gap-1.5">
        <label
          htmlFor="legacy-cert-file"
          className="text-sm font-medium text-[color:var(--text-primary)]"
        >
          Certificate file
        </label>
        <input
          id="legacy-cert-file"
          ref={inputRef}
          type="file"
          accept={FILE_ACCEPT}
          onChange={onFileChange}
          disabled={upload.isPending}
          className="peer sr-only"
        />
        <div
          onClick={() => {
            if (!upload.isPending) inputRef.current?.click();
          }}
          onDragOver={(event) => {
            event.preventDefault();
            if (!upload.isPending) setDragActive(true);
          }}
          onDragLeave={(event) => {
            event.preventDefault();
            setDragActive(false);
          }}
          onDrop={onDrop}
          className={cn(
            "flex flex-col items-center justify-center gap-2 rounded-xl border border-dashed px-4 py-6 text-center transition-colors peer-focus:border-[color:var(--border-brand-alt)] peer-focus:shadow-focus",
            upload.isPending
              ? "cursor-not-allowed border-[color:var(--border-strong)] bg-[color:var(--bg-secondary)] opacity-60"
              : "cursor-pointer border-[color:var(--border-strong)] bg-[color:var(--bg-secondary)] hover:border-[color:var(--border-brand-alt)] hover:bg-[color:var(--bg-tertiary)]",
            dragActive &&
              "border-[color:var(--border-brand-alt)] bg-[color:var(--bg-brand-primary)]",
          )}
        >
          {selectedFile ? (
            <>
              <FileText
                className="h-6 w-6 text-[color:var(--fg-brand-primary)]"
                aria-hidden="true"
              />
              <span className="break-all text-sm font-medium text-[color:var(--text-primary)]">
                {selectedFile.name}
              </span>
              <span className="text-xs text-[color:var(--text-tertiary)]">
                {formatBytes(selectedFile.size)} · Choose a different file
              </span>
            </>
          ) : (
            <>
              <Upload
                className="h-6 w-6 text-[color:var(--fg-quaternary)]"
                aria-hidden="true"
              />
              <span className="text-sm text-[color:var(--text-secondary)]">
                <span className="font-semibold text-[color:var(--text-brand-tertiary)]">
                  Choose a file
                </span>{" "}
                or drag it here
              </span>
            </>
          )}
        </div>
        <span className="text-xs text-[color:var(--text-tertiary)]">{HELPER_TEXT}</span>
      </div>
      {errorMessage ? (
        <Banner variant="warn" role="alert">
          {errorMessage}
        </Banner>
      ) : null}
      <div className="flex items-center gap-3">
        <Button
          type="button"
          variant="primary"
          onClick={onUpload}
          disabled={upload.isPending}
          aria-busy={upload.isPending}
        >
          {upload.isPending ? "Uploading…" : "Upload certificate"}
        </Button>
        {status !== "none" ? (
          <Button type="button" variant="ghost" onClick={cancelControl} disabled={upload.isPending}>
            Cancel
          </Button>
        ) : null}
      </div>
    </div>
  );

  let body: React.ReactNode;
  if (statusQuery.isError) {
    body = (
      <Banner variant="warn" role="alert">
        {toCertStatusErrorMessage()}
      </Banner>
    );
  } else if (!statusKnown) {
    // Error first, then EVERY other not-yet-answered shape — in flight, paused, or
    // disabled — under the one waiting message, so the fallback status below is never
    // reachable from a payload the card does not have.
    body = <p className="text-sm text-[color:var(--text-tertiary)]">Loading certificate status…</p>;
  } else if (showControl) {
    body = uploadControl;
  } else if (status === "pending") {
    body = (
      <div className="flex flex-col gap-3">
        {/* Success confirmation. `info` already resolves to role="status" (an implicit
            polite live region) in Banner; the explicit aria-live="polite" makes the SR
            announcement intent unambiguous across AT that don't infer it (PR #414). */}
        <Banner variant="info" role="status" aria-live="polite">
          Certificate uploaded. Awaiting verification by CITS.
        </Banner>
        <p className="text-sm text-[color:var(--text-secondary)]">
          <strong>Filename:</strong> {statusQuery.data?.file_name ?? "—"}
          <br />
          <strong>Uploaded:</strong> {formatDate(statusQuery.data?.uploaded_at)}
        </p>
        <div>
          <Button type="button" variant="secondary" onClick={openControl}>
            Replace file
          </Button>
        </div>
      </div>
    );
  } else if (status === "verified") {
    // gh-742 — verified arrives in TWO shapes. The ordinary one (upload → approve,
    // Story 16.4) carries the row's evidence and renders Screen 14 verbatim. The
    // off-band one has no row behind it at all: FR79 has CITS validate the
    // certificate before the contract, so `organizations/invite_clinician` credits
    // it at invite and never writes an upload — the GET reports `verified` with
    // every upload-derived field absent (`get_legacy_cert_status/endpoint.py`).
    //
    // State the verdict in both, but never render evidence that does not exist:
    // the dated sentence degrades to "Certificate verified by CITS on ." and the
    // block below to "Filename: —" with two blank dates, which reads as a broken
    // page and implies a record the platform never made. Both are keyed on the
    // payload's own fields, so this is one renderer of one server-side status —
    // NOT a second discriminator re-derived in the component (the DW-458 lesson).
    const verifiedOn = formatDate(statusQuery.data?.verified_at);
    const hasUploadEvidence = Boolean(
      statusQuery.data?.file_name ||
        statusQuery.data?.uploaded_at ||
        statusQuery.data?.verified_at,
    );
    body = (
      <div className="flex flex-col gap-3">
        <Banner variant="success">
          {verifiedOn
            ? `Certificate verified by CITS on ${verifiedOn}.`
            : "Certificate verified by CITS."}
        </Banner>
        {hasUploadEvidence ? (
          <p className="text-sm text-[color:var(--text-secondary)]">
            <strong>Filename:</strong> {statusQuery.data?.file_name ?? "—"}
            <br />
            <strong>Uploaded:</strong> {formatDate(statusQuery.data?.uploaded_at)}
            {verifiedOn ? (
              <>
                <br />
                <strong>Verified:</strong> {verifiedOn}
              </>
            ) : null}
          </p>
        ) : null}
      </div>
    );
  } else if (status === "rejected") {
    body = (
      <div className="flex flex-col gap-3">
        <Banner variant="warn" role="alert">
          Certificate could not be verified: {statusQuery.data?.rejection_reason ?? "please re-upload."}
        </Banner>
        <div>
          <Button type="button" variant="primary" onClick={openControl}>
            Upload a new certificate
          </Button>
        </div>
      </div>
    );
  }

  return (
    <section className="flex flex-col gap-4">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="text-lg font-semibold text-[color:var(--text-primary)]">
            Legacy AMPS certificate
          </h2>
          {/* gh-742 — the instruction belongs to the states that still want an
              upload. It is Screen 14b's copy (the legacy-PENDING profile); Screen 14,
              the verified profile, carries no equivalent product copy in this slot
              (its <p> there is an annotation about who sees the card at all) — so a
              verified reader is not told to upload a scan directly above a banner
              saying the certificate is already verified. Most visible on the off-band
              cohort, whose card has no upload anywhere on it, but the instruction was
              equally untrue for an ordinary approved clinician.
              Withheld until the status is KNOWN for the same reason the pill is: an
              instruction is a statement about the reader's state, and during the
              fetch we do not have one. */}
          {statusKnown && status !== "verified" ? (
            <p className="text-sm text-[color:var(--text-tertiary)]">
              Upload a scanned copy of your prior-AMPS certificate. A CITS admin reviews it
              manually — verification is not automated.
            </p>
          ) : null}
        </div>
        {/* gh-742 — the pill states a fact about the certificate, so it renders only
            once the fact is known. It sits OUTSIDE `body`, so before this it kept
            rendering the `?? "none"` fallback — "Awaiting upload" — through the whole
            fetch and through an outright error: the reported symptom, minus the box,
            flashed at every approved clinician on every cold load, and stayed on
            screen for anyone whose status read failed. */}
        {statusKnown ? <Badge variant={pill.variant}>{pill.text}</Badge> : null}
      </div>
      <Card>{body}</Card>
    </section>
  );
}
